import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { createPortal } from 'react-dom';

import { ModalProps } from './types';

import styles from './styles.module.scss';

const getOrCreateModalRoot = (): HTMLElement => {
  const existing = typeof document !== 'undefined' ? document.getElementById('app-modal-root') : null;
  if (existing) return existing;

  const el = document.createElement('div');
  el.id = 'app-modal-root';
  document.body.appendChild(el);
  return el;
};

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  closeOnOverlay = true,
  closeOnEsc = true,
}) => {
  const [shouldRender, setShouldRender] = useState(open);
  const [visible, setVisible] = useState(open);
  const portalRootRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    portalRootRef.current = getOrCreateModalRoot();

    const node = document.createElement('div');
    node.setAttribute('data-modal-container', 'true');
    containerRef.current = node;

    const root = portalRootRef.current!;
    root.appendChild(node);

    return () => {
      if (root.contains(node)) root.removeChild(node);
      containerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setVisible(false);
    }
  }, [open]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!shouldRender) return;

    const { body } = document;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = prevOverflow;
    };
  }, [shouldRender]);

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!closeOnEsc || !shouldRender) return;

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeOnEsc, shouldRender, onKeyDown]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!closeOnOverlay) return;
      if (e.target === e.currentTarget) onClose();
    },
    [closeOnOverlay, onClose],
  );

  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (!open) setShouldRender(false);
    },
    [open],
  );

  if (!shouldRender || !portalRootRef.current || !containerRef.current) return null;

  const modal = (
    <div
      className={clsx(styles.backdrop, visible && styles.open)}
      onMouseDown={handleBackdropClick}
      onTransitionEnd={handleTransitionEnd}
      role="presentation"
    >
      <div
        className={clsx(styles.modal, visible && styles.open)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        <div className={styles.header}>
          {title && (
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
          )}

          <button type="button" className={styles.closeBtn} aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );

  return createPortal(modal, containerRef.current);
};
